<script setup lang="ts">
import { useElementVisibility, useOnline } from "@vueuse/core";

type Props = {
  amountShown: number;
  totalCount: number;
  enableAutoscroll?: boolean;
  isFetching?: boolean;
};

const {
  enableAutoscroll = true,
  isFetching = false,
  amountShown,
  totalCount,
} = defineProps<Props>();

const emit = defineEmits<{
  (e: "load-more"): void;
}>();

const emitLoadMore = () => {
  emit("load-more");
};

const showMore = useTemplateRef("showMore");
const isVisible = useElementVisibility(showMore, {
  rootMargin: "0px 0px 200px 0px", // trigger when the element is within 200px of the viewport
});

const online = useOnline();

watch(isVisible, (newState) => {
  if (online.value && newState && enableAutoscroll && !isFetching) {
    emitLoadMore();
  }
});

const progressPercentage = computed(() => {
  return Math.round((amountShown / totalCount) * 100);
});
</script>

<template>
  <div
    ref="showMore"
    data-test="show-more"
    class="flex flex-col items-center py-8"
  >
    <I18nT
      scope="global"
      keypath="showMore.showingGeneral"
      tag="p"
      class="mb-4 leading-none"
    >
      <template #PLACEHOLDER_amountShown>
        <strong>{{ amountShown }}</strong>
      </template>
      <template #PLACEHOLDER_totalCount>
        <strong>{{ totalCount }}</strong>
      </template>
    </I18nT>

    <div class="mb-4 w-48 h-1 block bg-platform-bg-2 relative">
      <span
        data-test="show-more-progress"
        class="absolute h-full bg-platform-text-1 left-0 top-0 transition-all"
        :style="{
          width: `${progressPercentage}%`,
        }"
      />
    </div>

    <BaseButton
      data-cy="show-more-button"
      data-test="show-more-button"
      color-scheme="secondary"
      :is-loading="isFetching"
      @click="emitLoadMore"
    >
      {{ $t("showMore.showMoreGeneral") }}
    </BaseButton>
  </div>
</template>
